import React from 'react';
import { Button, notification } from 'antd';
import { I18n } from '@aws-amplify/core';

import { useFinishBeraJobSummary } from '@/hooks';
import { useBeraReportV2 } from '@/context/BeraReportV2';
import { useBeraJobStepsContext } from '@/components/views/BeraJob/context';

import { STRETCH_ISSUE_MESSAGE, findStretchIssue } from './coverage';
import { toSummaryScope } from './utils';
import { UNEVALUATED_MESSAGE } from './constants';
import { FooterRow } from './styles';

import type { StretchIssue } from './coverage';

interface FooterProps {
	onPrevious: () => void;
}

export function Footer({ onPrevious }: Readonly<FooterProps>): JSX.Element {
	const { handleShowBeraJobResultChange } = useBeraJobStepsContext();

	const {
		organizationId,
		companyId,
		jobSummary,
		allReports,
		allStretches,
		videoDuration,
		isVideoUnavailable,
		hasUnsavedNoRiskRemoval
	} = useBeraReportV2();

	const { mutateAsync: finishBeraJobSummary, isLoading: isFinishing } = useFinishBeraJobSummary();

	const hasEvaluatedReport = allReports.some((report) => !!report.is_evaluated);

	const canFinishReport = hasEvaluatedReport && !isVideoUnavailable && !hasUnsavedNoRiskRemoval;

	function notifyError(message: string): void {
		notification.error({ message: I18n.get(message) });
	}

	function notifyStretchIssue(issue: StretchIssue): void {
		notifyError(STRETCH_ISSUE_MESSAGE[issue]);
	}

	async function handleFinishReport(): Promise<void> {
		const stretchIssue = findStretchIssue({ stretches: allStretches, videoDuration });

		if (stretchIssue) {
			notifyStretchIssue(stretchIssue);
			return;
		}

		const hasUnevaluatedReport = allReports.some((report) => !report.is_evaluated);

		if (hasUnevaluatedReport) {
			notifyError(UNEVALUATED_MESSAGE);
			return;
		}

		const params = toSummaryScope({ summaryId: jobSummary.id, organizationId, companyId });

		const finished = await finishBeraJobSummary(params);

		if (!finished) {
			return;
		}

		handleShowBeraJobResultChange(true);
	}

	return (
		<FooterRow>
			<Button size="large" onClick={onPrevious}>
				{I18n.get('Back')}
			</Button>
			<Button
				size="large"
				type="primary"
				loading={isFinishing}
				disabled={!canFinishReport}
				onClick={handleFinishReport}
			>
				{I18n.get('Finish B.E.R.A.')}
			</Button>
		</FooterRow>
	);
}
